/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soonki Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import { forwardRef, useState } from "react";
import { useTheme } from "@emotion/react";

import { Maybe } from "@/frameworks/libraries";
import { Row } from "@/frameworks/designStuff/components";
import { Avatar, AvatarProps } from "../Avatar";
import { Icon } from "../Icon";
import * as S from "./private/emotion";
import { IconComponentProps } from "../Icon/Icon";

interface ChipProps extends React.HTMLAttributes<HTMLDivElement> {
	src?: string;
	text?: string;
	icon?: IconComponentProps;
	isError?: boolean;
	colorTheme?: "blue" | "purple" | "orange" | "selago" | "gray" | "red";
	handleClose?: (ev?: React.MouseEvent<SVGSVGElement>) => void;
	children: React.ReactNode;
	avatarKind?: AvatarProps["kind"];
	isEllipsisText?: boolean;
	textOnly?: boolean;
}

const Chip = forwardRef(
	(
		{
			src,
			text = "",
			colorTheme = "blue",
			children,
			handleClose,
			icon,
			avatarKind = "people_24",
			textOnly = false,
			isError = false,
			...rest
		}: ChipProps,
		ref
	) => {
		const theme = useTheme();
		const [imageSrc, setImageSrc] = useState<string | undefined>(src);

		return (
			<S.Wrapper
				verticalAlign="center"
				horizonAlign="center"
				gap={4}
				colortheme={colorTheme}
				hasavatar={`${imageSrc !== undefined || icon !== undefined}`}
				ref={ref}
				{...rest}
			>
				<Maybe test={!textOnly}>
					{icon && !imageSrc && (
						<Row
							verticalAlign="center"
							horizonAlign="center"
							css={{
								width: "24px",
								height: "24px",
								borderRadius: "50%",
								background: theme.colors.neutral_00_68
							}}
						>
							<Icon {...icon} ref={undefined} fill={isError ? "red" : "black"} />
						</Row>
					)}

					<Row
						css={{
							minWidth: "24px",
							minHeight: "24px",
							maxHeight: "24px",
							maxWidth: "24px",
							flex: "1 1 24px"
						}}
					>
						<Avatar
							src={imageSrc}
							kind={avatarKind}
							type="image"
							text={text}
							onErrorImage={() => {
								setImageSrc(undefined);
							}}
						/>
					</Row>
				</Maybe>

				<Row gap={6} verticalAlign="center" horizonAlign="center" className="wrap-text-chip">
					<span className="typo">{children}</span>
					<Row
						verticalAlign="center"
						horizonAlign="center"
						css={[
							{
								minWidth: "24px",
								minHeight: "24px",
								maxWidth: "24px",
								maxHeight: "24px",
								flex: "1 1 24px"
							},
							S.IconCss
						]}
					>
						<Icon name="Close24" size={16} fill={theme.colors.neutral_80_100} onClick={handleClose} />
					</Row>
				</Row>
			</S.Wrapper>
		);
	}
);

/**
 * forwardRef의 경우 export default function으로 작업했을때,
 * d.ts파일에서 컴포넌트명이 _default로 선언되서 export 방식을 수정했습니다.
 */
export default Chip;
